import '../i18n';

import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';

import { createBadge, deleteBadge, updateBadge } from '../actions/badges';
import { getMyContacts } from '../actions/contacts';
import FormCheckbox from '../components/FormCheckbox';
import globalStyles from '../stylesheets/globalStyles';
import DocumentMaintainArea from './DocumentMaintainArea';
import FormInput from './FormInput';
import StatusAlerts from './StatusAlerts';

const i18nPrefix = 'EditBadgeModal';

const emptyBadge = {
  contact_id: null,
  badge_number: '',
  comment: '',
  issue_date: '',
  deposit_amount: '',
  redemption_date: null,
  deposit_refunded: false,
};

const getValidationErrors = (badge) => {
  const errors = {};
  errors.contact_id = !badge.contact_id ? '*' : false;
  errors.badge_number = !badge.badge_number ? '*' : false;
  errors.issue_date = !badge.issue_date ? '*' : false;
  errors.deposit_amount =
    badge.deposit_amount === '' || badge.deposit_amount === null ? '*' : false;
  return errors;
};

class EditBadgeModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      success: false,
      error: false,
      errorMessage: '',
      show: false,
      edit: false,
      badge: emptyBadge,
      contacts: [],
    };
  }

  componentDidMount() {
    this.props.onRef && this.props.onRef(this);
    this.reloadContacts();
  }

  reloadContacts = (callback) => {
    getMyContacts(
      (contacts) => {
        this.setState({ contacts: contacts || [] }, () => {
          callback && callback(contacts || []);
        });
      },
      () => {},
    );
  };

  show = (badge = {}) => {
    const nextBadge = {
      ...emptyBadge,
      ...badge,
      comment: badge.comment || '',
      redemption_date: badge.redemption_date || null,
      deposit_refunded: !!badge.deposit_refunded,
    };

    this.reloadContacts((contacts) => {
      const hasSelectedContact =
        nextBadge.contact_id &&
        contacts.some(
          (contact) => String(contact.id) === String(nextBadge.contact_id),
        );

      if (!hasSelectedContact && badge.contact && badge.contact.id) {
        this.setState((prevState) => ({
          contacts: [...prevState.contacts, badge.contact],
        }));
      }
    });

    this.setState({
      show: true,
      edit: !!badge.id,
      success: false,
      error: false,
      loading: false,
      badge: nextBadge,
    });
  };

  handleClose = () => {
    this.setState({
      show: false,
      badge: emptyBadge,
      edit: false,
      loading: false,
      success: false,
      error: false,
      errorMessage: '',
    });
    this.props.onClose && this.props.onClose();
  };

  handleSave = () => {
    this.props.onSave && this.props.onSave();
    this.handleClose();
  };

  createButtonClicked = () => {
    const formErrors = getValidationErrors(this.state.badge);
    const noErrors = Object.values(formErrors).every((x) => x === false);

    if (!noErrors) {
      this.setState({
        error: true,
        errorMessage: this.props.t('errors.someValuesAreNotCorrect'),
      });
      return;
    }

    this.setState({ loading: true, error: false, success: false }, () => {
      createBadge(
        this.state.badge,
        () => {
          this.setState({ loading: false, success: true, error: false });
          this.handleSave();
        },
        (error) => {
          const errorMessage =
            error?.response?.data?.language_variable &&
            this.props.t(`errors.${error.response.data.language_variable}`);

          this.setState({
            loading: false,
            success: false,
            error: true,
            errorMessage:
              errorMessage || this.props.t('errors.somethingWentWrong'),
          });
        },
      );
    });
  };

  updateButtonClicked = () => {
    const formErrors = getValidationErrors(this.state.badge);
    const noErrors = Object.values(formErrors).every((x) => x === false);

    if (!noErrors) {
      this.setState({
        error: true,
        errorMessage: this.props.t('errors.someValuesAreNotCorrect'),
      });
      return;
    }

    this.setState({ loading: true, error: false, success: false }, () => {
      updateBadge(
        this.state.badge,
        () => {
          this.setState({ loading: false, success: true, error: false });
          this.handleSave();
        },
        (error) => {
          const errorMessage =
            error?.response?.data?.language_variable &&
            this.props.t(`errors.${error.response.data.language_variable}`);

          this.setState({
            loading: false,
            success: false,
            error: true,
            errorMessage:
              errorMessage || this.props.t('errors.somethingWentWrong'),
          });
        },
      );
    });
  };

  deleteButtonClicked = () => {
    if (
      !window.confirm(
        this.props.t(`${i18nPrefix}.areYouSureDelete`, 'Delete badge?'),
      )
    ) {
      return;
    }

    this.setState({ loading: true, error: false, success: false }, () => {
      deleteBadge(
        this.state.badge.id,
        () => {
          this.setState({ loading: false, success: true, error: false });
          this.handleSave();
        },
        () => {
          this.setState({
            loading: false,
            success: false,
            error: true,
            errorMessage: this.props.t('errors.somethingWentWrong'),
          });
        },
      );
    });
  };

  render() {
    const { t } = this.props;
    const { badge } = this.state;
    const formErrors = getValidationErrors(badge);

    return (
      <Modal
        show={this.state.show}
        onHide={this.handleClose}
        animation={false}
        size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {this.state.edit
              ? t(`${i18nPrefix}.editBadge`, 'Edit badge')
              : t(`${i18nPrefix}.createBadge`, 'Create badge')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <StatusAlerts
            state={this.state}
            errorMessage={this.state.errorMessage}
          />

          <FormInput
            id='badge_contact_id'
            as='select'
            label={t(`${i18nPrefix}.contact`, 'Contact')}
            value={badge.contact_id}
            error={formErrors.contact_id}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  contact_id: event.target.value || null,
                },
              });
            }}>
            <option value=''>{t('global.pleaseSelect')}</option>
            {[...this.state.contacts]
              .sort((a, b) => (a.lastname > b.lastname ? 1 : -1))
              .map((contact) => (
                <option
                  key={contact.id}
                  value={
                    contact.id
                  }>{`${contact.lastname} ${contact.firstname} (${contact.street} ${contact.zip}, ${contact.city})`}</option>
              ))}
          </FormInput>

          <FormInput
            id='badge_number'
            label={t(`${i18nPrefix}.badgeNumber`, 'Badge number')}
            value={badge.badge_number}
            error={formErrors.badge_number}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  badge_number: event.target.value,
                },
              });
            }}
          />

          <FormInput
            id='badge_issue_date'
            type='date'
            label={t(`${i18nPrefix}.issueDate`, 'Issue date')}
            value={badge.issue_date}
            error={formErrors.issue_date}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  issue_date: event.target.value,
                },
              });
            }}
          />

          <FormInput
            id='badge_comment'
            as='textarea'
            type='text'
            label={t(`${i18nPrefix}.comment`, 'Comment')}
            value={badge.comment}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  comment: event.target.value,
                },
              });
            }}
          />

          <FormInput
            id='badge_deposit_amount'
            type='number'
            step='1.'
            label={t(`${i18nPrefix}.depositAmount`, 'Deposit amount')}
            value={badge.deposit_amount}
            error={formErrors.deposit_amount}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  deposit_amount: event.target.value,
                },
              });
            }}
          />

          <FormInput
            id='badge_redemption_date'
            type='date'
            label={t(`${i18nPrefix}.redemptionDate`, 'Redemption date')}
            value={badge.redemption_date}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  redemption_date: event.target.value || null,
                },
              });
            }}
          />

          <FormCheckbox
            id='badge_deposit_refunded'
            label={t(`${i18nPrefix}.depositRefunded`, 'Deposit refunded')}
            checked={!!badge.deposit_refunded}
            onChange={(event) => {
              this.setState({
                badge: {
                  ...badge,
                  deposit_refunded: event.target.checked,
                },
              });
            }}
            last
          />

          {this.state.edit && this.state.badge.id && (
            <div style={{ marginTop: 24 }}>
              <DocumentMaintainArea
                id_params={{ badge_id: this.state.badge.id }}
              />
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {this.state.edit && (
            <Button
              style={globalStyles.deleteButton}
              disabled={this.state.loading}
              onClick={this.deleteButtonClicked}>
              {t('global.delete')}
            </Button>
          )}

          <Button
            style={globalStyles.saveButton}
            disabled={this.state.loading}
            onClick={
              this.state.edit
                ? this.updateButtonClicked
                : this.createButtonClicked
            }>
            {this.state.edit ? t('global.save') : t('global.create')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withTranslation()(EditBadgeModal);
